import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Dialog, TextField, Alert, Typography, Grid, MenuItem, Divider, Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import { superAdminAPI } from '../../services/api';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import {
  SuperAdminDataTable, apiErrorMessage, statusChip, AdminTableEditButton, STATUS_FILTER_OPTIONS,
} from '../../components/superadmin/SuperAdminDataTable';
import { SuperAdminDetailDrawer, SuperAdminDetailField } from '../../components/superadmin/SuperAdminDetailDrawer';
import { SubscriptionPlanGrid } from '../../components/superadmin/SubscriptionPlanCard';
import { useLocale } from '../../contexts/LocaleContext';
import { RADIUS, brandColors, brandCss } from '../../theme/appTheme';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';

const defaultPlanForm = {
  planName: '',
  description: '',
  hsnSac: '998314',
  monthlyPrice: 0,
  yearlyPrice: 0,
  userLimit: 5,
  vehicleLimit: 10,
  driverLimit: 20,
  branchLimit: 2,
  gpsDeviceLimit: 5,
  storageLimit: 5,
  status: 'active',
};

export const PlansPage: React.FC = () => {
  const [plans, setPlans] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(defaultPlanForm);
  const [message, setMessage] = useState('');
  const [detailRow, setDetailRow] = useState<any>(null);
  const { formatCurrency } = useLocale();

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await superAdminAPI.getPlans();
      setPlans(res.data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load plans'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const active = plans.filter((p) => p.status === 'active').length;
    const inactive = plans.filter((p) => p.status === 'inactive').length;
    const avgMonthly = plans.length
      ? Math.round(plans.reduce((s, p) => s + (p.monthlyPrice || 0), 0) / plans.length)
      : 0;
    return { total: plans.length, active, inactive, avgMonthly };
  }, [plans]);

  const openCreate = () => {
    setEditId(null);
    setForm(defaultPlanForm);
    setOpen(true);
  };

  const openEdit = (plan: any) => {
    setEditId(plan._id);
    setForm({
      planName: plan.planName || '',
      description: plan.description || '',
      hsnSac: plan.hsnSac || '998314',
      monthlyPrice: plan.monthlyPrice ?? 0,
      yearlyPrice: plan.yearlyPrice ?? 0,
      userLimit: plan.userLimit ?? 5,
      vehicleLimit: plan.vehicleLimit ?? 10,
      driverLimit: plan.driverLimit ?? 20,
      branchLimit: plan.branchLimit ?? 2,
      gpsDeviceLimit: plan.gpsDeviceLimit ?? 5,
      storageLimit: plan.storageLimit ?? 5,
      status: plan.status || 'active',
    });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editId) {
        await superAdminAPI.updatePlan(editId, form);
        setMessage('Plan updated');
      } else {
        await superAdminAPI.createPlan(form);
        setMessage('Plan created');
      }
      setOpen(false);
      setEditId(null);
      setForm(defaultPlanForm);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, editId ? 'Update failed' : 'Create failed'));
    }
  };

  const togglePlanStatus = async (plan: any) => {
    try {
      const next = plan.status === 'active' ? 'inactive' : 'active';
      await superAdminAPI.updatePlan(plan._id, { status: next });
      setMessage(`Plan marked ${next}`);
      load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Update failed'));
    }
  };

  const buildPlanFields = (p: any): SuperAdminDetailField[] => [
    { label: 'Plan Name', value: p.planName },
    { label: 'Description', value: p.description || '—', fullWidth: true },
    { label: 'HSN / SAC', value: p.hsnSac || '998314' },
    { label: 'Monthly Price', value: formatCurrency(p.monthlyPrice) },
    { label: 'Yearly Price', value: formatCurrency(p.yearlyPrice) },
    { label: 'User Limit', value: p.userLimit },
    { label: 'Vehicle Limit', value: p.vehicleLimit },
    { label: 'Driver Limit', value: p.driverLimit },
    { label: 'Branch Limit', value: p.branchLimit },
    { label: 'GPS Device Limit', value: p.gpsDeviceLimit },
    { label: 'Storage Limit (GB)', value: p.storageLimit },
    { label: 'Plan ID', value: String(p._id || ''), fullWidth: true },
  ];

  return (
    <Box>
      <PageHeader
        title="Subscription Plans"
        subtitle={getPageSubtitle('plans')}
        icon={<CardMembershipIcon />}

        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={catchyPrimaryButtonSx}>
            Add Plan
          </Button>
        }
      />
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Plans', value: stats.total, color: brandCss.primary },
          { label: 'Active', value: stats.active, color: '#1565c0' },
          { label: 'Inactive', value: stats.inactive, color: '#f59e0b' },
          { label: 'Avg Monthly Price', value: formatCurrency(stats.avgMonthly), color: '#3b82f6' },
        ].map((s) => (
          <Grid item xs={6} md={3} key={s.label}>
            <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${brandColors.border}` }}>
              <Typography fontSize={11} fontWeight={700} color="text.secondary">{s.label}</Typography>
              <Typography fontWeight={800} fontSize="1.4rem" sx={{ color: s.color }}>{s.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {!loading && plans.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography fontWeight={700} fontSize={13} color={brandCss.primaryDark} mb={1.5} letterSpacing={0.5}>
            PLAN OVERVIEW
          </Typography>
          <SubscriptionPlanGrid
            plans={plans}
            formatMoney={formatCurrency}
            onEdit={openEdit}
            onToggleStatus={togglePlanStatus}
          />
        </Box>
      )}

      <Typography fontWeight={700} fontSize={13} color={brandCss.primaryDark} mb={1.5} letterSpacing={0.5}>
        ALL PLANS
      </Typography>

      <SuperAdminDataTable
        rows={plans}
        loading={loading}
        error={error}
        exportFileName="super-admin-plans"
        exportSheetName="Plans"
        statusField="status"
        searchPlaceholder="Search plan name, HSN, description..."
        columns={[
          {
            id: 'name',
            header: 'Plan',
            searchValue: (p) => `${p.planName} ${p.description || ''}`,
            exportValue: (p) => p.planName,
            render: (p) => (
              <Box>
                <Typography fontWeight={600} fontSize={13}>{p.planName}</Typography>
                {p.description && (
                  <Typography fontSize={11} color="text.secondary">{p.description}</Typography>
                )}
              </Box>
            ),
          },
          {
            id: 'hsn',
            header: 'HSN/SAC',
            searchValue: (p) => p.hsnSac,
            exportValue: (p) => p.hsnSac,
            render: (p) => <Typography fontSize={13}>{p.hsnSac || '998314'}</Typography>,
          },
          {
            id: 'monthly',
            header: 'Monthly',
            searchValue: (p) => String(p.monthlyPrice),
            exportValue: (p) => p.monthlyPrice,
            render: (p) => formatCurrency(p.monthlyPrice),
          },
          {
            id: 'yearly',
            header: 'Yearly',
            searchValue: (p) => String(p.yearlyPrice),
            exportValue: (p) => p.yearlyPrice,
            render: (p) => formatCurrency(p.yearlyPrice),
          },
          {
            id: 'limits',
            header: 'Limits',
            exportValue: (p) => `U${p.userLimit} V${p.vehicleLimit} D${p.driverLimit}`,
            render: (p) => (
              <Typography fontSize={12} color="text.secondary">
                {p.userLimit} users · {p.vehicleLimit} vehicles · {p.driverLimit} drivers
              </Typography>
            ),
          },
          {
            id: 'status',
            header: 'Status',
            exportValue: (p) => p.status,
            filterType: 'select',
            filterOptions: STATUS_FILTER_OPTIONS,
            filterValue: (p) => p.status,
            render: (p) => statusChip(p.status),
          },
        ]}
        onRowClick={setDetailRow}
        renderActions={(p) => (
          <AdminTableEditButton onClick={() => openEdit(p)} title="Edit plan" />
        )}
      />

      <SuperAdminDetailDrawer
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        moduleTitle="Subscription Plan"
        title={detailRow?.planName || ''}
        subtitle={detailRow?.description}
        status={detailRow?.status}
        fields={detailRow ? buildPlanFields(detailRow) : []}
        primaryActions={detailRow ? (
          <Button variant="contained" onClick={() => { openEdit(detailRow); setDetailRow(null); }} sx={catchyPrimaryButtonSx}>
            Edit Plan
          </Button>
        ) : undefined}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: `${RADIUS}px` } }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={0.5}>
            {editId ? 'Edit Subscription Plan' : 'Create Subscription Plan'}
          </Typography>
          <Typography fontSize={13} color="text.secondary" mb={2}>
            Plan name appears on tenant invoices. HSN/SAC is used on platform tax invoices.
          </Typography>

          <Typography fontWeight={700} fontSize={12} color={brandCss.primaryDark} mb={1}>PLAN DETAILS</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField label="Plan Name" value={form.planName}
                onChange={(e) => setForm({ ...form, planName: e.target.value })}
                fullWidth size="small" sx={catchyTextFieldSx}
                helperText="Invoice line item — e.g. CRM Development" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="HSN / SAC" value={form.hsnSac}
                onChange={(e) => setForm({ ...form, hsnSac: e.target.value })}
                fullWidth size="small" sx={catchyTextFieldSx} helperText="e.g. 998314" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Description" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                fullWidth size="small" sx={catchyTextFieldSx} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />
          <Typography fontWeight={700} fontSize={12} color={brandCss.primaryDark} mb={1}>PRICING</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField label="Monthly Price" type="number" value={form.monthlyPrice}
                onChange={(e) => setForm({ ...form, monthlyPrice: Number(e.target.value) })}
                fullWidth size="small" sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Yearly Price" type="number" value={form.yearlyPrice}
                onChange={(e) => setForm({ ...form, yearlyPrice: Number(e.target.value) })}
                fullWidth size="small" sx={catchyTextFieldSx} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />
          <Typography fontWeight={700} fontSize={12} color={brandCss.primaryDark} mb={1}>LIMITS</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField label="User Limit" type="number" value={form.userLimit}
                onChange={(e) => setForm({ ...form, userLimit: Number(e.target.value) })}
                fullWidth size="small" sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Vehicle Limit" type="number" value={form.vehicleLimit}
                onChange={(e) => setForm({ ...form, vehicleLimit: Number(e.target.value) })}
                fullWidth size="small" sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Driver Limit" type="number" value={form.driverLimit}
                onChange={(e) => setForm({ ...form, driverLimit: Number(e.target.value) })}
                fullWidth size="small" sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Branch Limit" type="number" value={form.branchLimit}
                onChange={(e) => setForm({ ...form, branchLimit: Number(e.target.value) })}
                fullWidth size="small" sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="GPS Device Limit" type="number" value={form.gpsDeviceLimit}
                onChange={(e) => setForm({ ...form, gpsDeviceLimit: Number(e.target.value) })}
                fullWidth size="small" sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Storage Limit (GB)" type="number" value={form.storageLimit}
                onChange={(e) => setForm({ ...form, storageLimit: Number(e.target.value) })}
                fullWidth size="small" sx={catchyTextFieldSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField select label="Status" value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                fullWidth size="small" sx={catchyTextFieldSx}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} sx={catchyPrimaryButtonSx}>
              {editId ? 'Save Changes' : 'Create Plan'}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};
